import {
  getV1CompaniesIdEquipmentTypes,
  OrderUnitOfMeasure,
  patchV1CompaniesCompanyIdEquipmentTypesId,
  postV1CompaniesCompanyIdEquipmentTypes,
} from '@treadinc/horizon-api-spec';
import { t } from 'i18next';

import { EquipmentTypeItem } from '~hooks/useEquipment/models';
import { CSVColumn } from '~utils/csv/csvProcessor';
import { fetchAllApiPages } from '~utils/fetchAllPages';

import { EntityCSVConfig } from './types';

const equipmentTypesColumns: CSVColumn[] = [
  {
    key: 'id',
    header: 'id',
    required: false,
    exampleValue: '',
    description: 'Existing ID (for updates only)',
  },
  {
    key: 'name',
    header: 'name',
    required: true,
    exampleValue: 'Dump Truck',
    description: 'Equipment type name (must be unique)',
  },
  {
    key: 'external_id',
    header: 'external_id',
    required: false,
    exampleValue: 'EQT-001',
    description: 'Your internal reference ID',
  },
  {
    key: 'accounting_id',
    header: 'accounting_id',
    required: false,
    exampleValue: 'ACCT-001',
    description: 'Accounting system reference',
  },
  {
    key: 'gross_capacity',
    header: 'gross_capacity',
    required: false,
    exampleValue: '20',
    description: 'Capacity value (min 0.01)',
  },
  {
    key: 'gross_capacity_uom',
    header: 'gross_capacity_uom',
    required: false,
    exampleValue: 'ton',
    description: 'Unit of measure (must exist in system)',
  },
  {
    key: 'materials',
    header: 'materials',
    required: false,
    exampleValue: 'Gravel; Sand',
    description: 'Semicolon-separated material names this type may haul (blank = any)',
  },
];

// Material names may contain commas, so a comma can't separate them.
const MATERIAL_NAME_SEPARATOR = ';';

const toMaterialIds = (
  materialNames: string,
  requireMaterialId: (name: string) => string | undefined,
) =>
  materialNames.split(MATERIAL_NAME_SEPARATOR).flatMap((name) => {
    const materialId = requireMaterialId(name);

    return materialId ? [materialId] : [];
  });

export const createEquipmentTypesConfig = (
  getUomByName: (name: string) => string | undefined,
  requireMaterialId: (name: string) => string | undefined,
  scopedCompanyId: string,
): EntityCSVConfig<EquipmentTypeItem> => ({
  entityType: 'equipment_types',
  columns: equipmentTypesColumns,

  fetchAll: (companyId: string) =>
    fetchAllApiPages(
      (query) =>
        getV1CompaniesIdEquipmentTypes({
          path: { 'company-id': companyId },
          query,
        }),
      EquipmentTypeItem.parse,
    ),

  create: async (
    companyId: string,
    data: Record<string, string>,
  ): Promise<EquipmentTypeItem> => {
    const grossCapacityUom = data.gross_capacity_uom
      ? getUomByName(data.gross_capacity_uom)
      : undefined;

    const response = await postV1CompaniesCompanyIdEquipmentTypes({
      path: { 'company-id': companyId },
      body: {
        name: data.name,
        external_id: data.external_id || undefined,
        accounting_id: data.accounting_id || undefined,
        gross_capacity: data.gross_capacity ? Number(data.gross_capacity) : undefined,
        gross_capacity_unit_of_measure: grossCapacityUom as OrderUnitOfMeasure,
        material_ids: data.materials
          ? toMaterialIds(data.materials, requireMaterialId)
          : undefined,
      },
    });

    return EquipmentTypeItem.parse(response.data.data);
  },

  update: async (
    id: string,
    data: Record<string, string>,
  ): Promise<EquipmentTypeItem> => {
    const grossCapacityUom = data.gross_capacity_uom
      ? getUomByName(data.gross_capacity_uom)
      : undefined;

    const response = await patchV1CompaniesCompanyIdEquipmentTypesId({
      path: { 'company-id': scopedCompanyId, id },
      body: {
        name: data.name,
        external_id: data.external_id || undefined,
        accounting_id: data.accounting_id || undefined,
        gross_capacity: data.gross_capacity ? Number(data.gross_capacity) : undefined,
        gross_capacity_unit_of_measure: grossCapacityUom as OrderUnitOfMeasure,
        // A blank cell clears the materials; a file without the column leaves them alone.
        material_ids:
          'materials' in data
            ? toMaterialIds(data.materials, requireMaterialId)
            : undefined,
      },
    });

    return EquipmentTypeItem.parse(response.data.data);
  },

  transformExport: (equipmentType: EquipmentTypeItem): Record<string, string> => ({
    id: equipmentType.id,
    name: equipmentType.name,
    external_id: equipmentType.externalId || '',
    accounting_id: equipmentType.accountingId || '',
    gross_capacity: equipmentType.grossCapacity || '',
    gross_capacity_uom: equipmentType.grossCapacityUnitOfMeasure?.name || '',
    materials: equipmentType.materials
      .map((material) => material.name)
      .join(`${MATERIAL_NAME_SEPARATOR} `),
  }),

  transformImport: (row: Record<string, string>): Record<string, unknown> => ({
    name: row.name?.trim(),
    external_id: row.external_id?.trim() || null,
    accounting_id: row.accounting_id?.trim() || null,
    gross_capacity: row.gross_capacity ? Number(row.gross_capacity) : null,
    gross_capacity_uom: row.gross_capacity_uom?.trim() || null,
    materials: row.materials?.trim() || null,
  }),

  validateRow: (row: Record<string, string>): string[] => {
    const errors: string[] = [];

    if (!row.name?.trim()) {
      errors.push(t('csv_import_export.errors.required_field', { field: 'name' }));
    }

    if (row.gross_capacity) {
      const capacity = Number(row.gross_capacity);
      if (isNaN(capacity)) {
        errors.push(
          t('csv_import_export.errors.invalid_number', { field: 'gross_capacity' }),
        );
      } else if (capacity < 0.01) {
        errors.push(
          t('csv_import_export.errors.number_min', {
            field: 'gross_capacity',
            min: 0.01,
          }),
        );
      }
    }

    return errors;
  },
});
